import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import EventVote from "./EventVote";
import EventMatchmaker from "./EventMatchmaker";
import { Trash, Trophy, Spinner, Fire } from "@phosphor-icons/react";

const API_BASE_URL = "https://warfc.com/api";

/* ---------------------------------------------------------
   FIX: StrictModeDroppable for React 18
--------------------------------------------------------- */
const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  if (!enabled) return null;
  return <Droppable {...props}>{children}</Droppable>;
};

/* ---------------------------------------------------------
   Utility: Fighter Image Resolver
--------------------------------------------------------- */
const getFighterImage = (f) => {
  if (!f) return "/default-fighter.png";
  const img = f.image_url || f.image;
  if (!img) return "/default-fighter.png";
  return img.startsWith("http") ? img : `${API_BASE_URL}/${img.replace(/^\//, "")}`;
};

const getPosterURL = (path) => {
  if (!path) return "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=2000&auto=format&fit=crop";
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  return `https://warfc.com/api/${cleanPath}`;
};

/* ---------------------------------------------------------
   Main Component: EventFightCard
--------------------------------------------------------- */
export default function EventFightCard() {
  const { eventId } = useParams();
  const { user, token } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const [f1, setF1] = useState(null);
  const [f2, setF2] = useState(null);
  const [weight, setWeight] = useState("");
  const [creating, setCreating] = useState(false);
  const [fights, setFights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState([]);
  const [event, setEvent] = useState(null);
  const [boutClass, setBoutClass] = useState("A"); 
  const [rounds, setRounds] = useState("3x2");
  const [isMobile, setIsMobile] = useState(
  typeof window !== "undefined" ? window.innerWidth < 768 : false
);
  const [liveVotes, setLiveVotes] = useState({});

const handleVotesUpdate = useCallback((fightId, voteData) => {
  setLiveVotes(prev => {
    const fid = String(fightId);
    return {
      ...prev,
      [fid]: {
        ...prev[fid],
        ...voteData, // This contains red_votes, blue_votes, etc. from the server response
        user_vote: voteData.user_vote 
      }
    };
  });
}, []);

useEffect(() => {
  const onResize = () => setIsMobile(window.innerWidth < 768);
  window.addEventListener("resize", onResize);
  return () => window.removeEventListener("resize", onResize);
}, []);
  // REPLACE THIS BLOCK
const fetchFights = useCallback(async () => {
  try {
    setLoading(true);
    const res = await axios.get(`${API_BASE_URL}/events/${eventId}/fights`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    const fightData = Array.isArray(res.data) ? res.data : (res.data.fights || []);

const initialVotes = {};
fightData.forEach(f => {
  // Use a variable to ensure consistency
  const fid = String(f.fight_id); 
  initialVotes[fid] = {
  red_percent: Number(f.red_percent) || 50,
  blue_percent: Number(f.blue_percent) || 50,
  total_votes: Number(f.total_votes) || 0,
  red_votes: Number(f.red_votes) || 0,
  blue_votes: Number(f.blue_votes) || 0,
  user_vote: f.user_vote 
};
});

    setLiveVotes(initialVotes); 
    setFights(fightData);
  } catch (err) {
    console.error("Fetch Error:", err);
  } finally {
    setLoading(false);
  }
}, [eventId, token]);

  const fetchEvent = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/events/${eventId}`);
      setEvent(res.data.event);
    } catch (err) {
      console.error("Fetch Event Error:", err);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
    fetchFights();
  }, [fetchEvent, fetchFights]);

  const sortedFights = useMemo(() => {
    return [...fights].sort((a, b) => (b.position || 0) - (a.position || 0));
  }, [fights]);

  const onDragEnd = async (result) => {
    if (!result.destination || !isAdmin) return;
    const reorderedFights = Array.from(sortedFights);
    const [removed] = reorderedFights.splice(result.source.index, 1);
    reorderedFights.splice(result.destination.index, 0, removed);

    const updatedFights = reorderedFights.map((fight, index) => ({
      ...fight,
      position: reorderedFights.length - index
    }));
    setFights(updatedFights);

    try {
      await axios.post(`${API_BASE_URL}/events/${eventId}/fights/reorder`, {
        fights: updatedFights.map(f => ({ fight_id: f.fight_id, position: f.position }))
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      console.error("Failed to save new order:", err);
      fetchFights(); 
    }
  };

const normalizeClass = (val) => {
  if (!val) return null;
  const v = String(val).toLowerCase().trim();
  if (v === "a") return "A";
  if (v === "b") return "B";
  if (v === "c") return "C";
  return null;
};
 

  useEffect(() => {
  const fetchAllRankings = async () => {
    try {
      const [seniorRes, youthRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/rankings`, { params: { category: "senior" } }),
        axios.get(`${API_BASE_URL}/rankings`, { params: { category: "youth" } })
      ]);
      
      const seniorData = Array.isArray(seniorRes.data) ? seniorRes.data : [];
      const youthData = Array.isArray(youthRes.data) ? youthRes.data : [];
      
      const combined = [...seniorData, ...youthData];
      console.log("Rankings Loaded:", combined.length); // DEBUG THIS
      setRankings(combined);
    } catch (err) { 
      console.error("Ranking fetch error", err); 
    }
  };
  fetchAllRankings();
}, []);


  const rankingMap = useMemo(() => {
  const map = {};
  rankings.forEach(r => {
    const id = String(r.fighter_id || "").trim();
    if (!id) return;

    // Normalize the class from the ranking entry (e.g., "youth" -> "N")
    const isYouth = r.category === "youth" || r.age_group === "youth";
    const entryClass = isYouth ? "N" : normalizeClass(r.class_division);
if (!entryClass) return;

    const key = `${id}-${entryClass}`;
    
    // Store the best rank for THAT specific class
    if (!map[key] || r.rank < map[key]) {
      map[key] = r.rank;
    }
  });
  return map;
}, [rankings]);

  const getFightRank = (fighterId, division) => {
  const key = `${String(fighterId)}-${division || "C"}`;
  return rankingMap[key] || null;
};

  const handleUpdateStatus = async (id, updates) => {
  // 1. OPTIMISTIC UPDATE: Update the UI immediately
  setFights(prevFights => 
    prevFights.map(f => 
      f.fight_id === id ? { ...f, ...updates } : f
    )
  );

  try {
    // 2. BACKGROUND SYNC: Update the server
    await axios.patch(`${API_BASE_URL}/events/fights/${id}`, updates, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    // We don't call fetchFights() here anymore to avoid the "Loading..." flicker.
    // The local state is already correct!
  } catch (err) {
    console.error("Update Error:", err.response?.data);
    // 3. ROLLBACK: If the server fails, refresh to show the real data
    alert(`Update Failed. Reverting...`);
    fetchFights(); 
  }
};

  const handleAddBout = async () => {
  if (!f1 || !f2 || !weight) return;
  setCreating(true);

  try {
    await axios.post(`${API_BASE_URL}/events/${eventId}/fights`, {
      fighter1_id: f1.fighter_id, 
      fighter2_id: f2.fighter_id,
      weightClass: weight, 
      division: boutClass, // Use the state variable directly
      rounds: rounds,      
      position: fights.length + 1
    }, { headers: { Authorization: `Bearer ${token}` } });
    
    setF1(null); setF2(null); setWeight(""); setRounds("3x2");
    fetchFights();
  } catch (err) { 
    alert("Failed to save bout."); 
  } finally { 
    setCreating(false); 
  }
};

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-cyan-500 pb-20 font-sans">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-red-600/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] animate-pulse delay-1000" />
      </div>

      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <header className="text-center pt-16 mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 mb-4">
            <Trophy size={14} className="text-yellow-500" />
            <span className="text-[10px] font-black tracking-[0.4em] uppercase text-gray-400">War Fighting Championship</span>
          </div>
          <h1 className="text-7xl md:text-[10rem] font-black italic uppercase tracking-tighter leading-none mb-4">
            FIGHT<span className="text-cyan-500">CARD</span>
          </h1>
        </header>

        {event && (
          <div className="relative w-full h-[340px] mb-10 overflow-hidden rounded-[2rem] border border-white/10">
            <img src={getPosterURL(event.image_url)} className="absolute inset-0 w-full h-full object-cover" alt={event.name} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-10">
              <span className="text-cyan-500 text-xs font-bold uppercase tracking-widest">{event.ruleSet}</span>
              <h1 className="text-4xl md:text-6xl font-black italic uppercase leading-none">{event.name}</h1>
              <div className="flex gap-6 text-sm text-white/70 mt-4 font-bold">
                <span>{new Date(event.date).toDateString()}</span>
                <span>{event.location}</span>
              </div>
            </div>
          </div>
        )}

  {isAdmin && (
  <EventMatchmaker
    token={token}
    rankings={rankings}
    boutClass={boutClass}
    setBoutClass={setBoutClass}
    f1={f1}
    setF1={setF1}
    f2={f2}
    setF2={setF2}
    rounds={rounds}
    setRounds={setRounds}
    weight={weight}
    setWeight={setWeight}
    creating={creating}
    onCreateBout={handleAddBout}
  />
)}
        <div className="space-y-24 md:space-y-32">
          {loading ? (
            <div className="text-center py-40"><Spinner className="animate-spin text-cyan-500 mx-auto mb-4" size={40} /><p className="font-black uppercase tracking-[0.3em] text-neutral-600">Syncing Card</p></div>
          ) : sortedFights.length > 0 ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <StrictModeDroppable droppableId="fight-card-list">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-24 md:space-y-32">
{/* 1. UPDATE THE KEYFRAMES IN YOUR STYLE TAG */}
<style dangerouslySetInnerHTML={{ __html: `
/* 1. CORE VS PULSE */
@keyframes vs-pulse {
  0% { transform: scale(1); opacity: 0.8; filter: brightness(1); }
  50% { transform: scale(1.1); opacity: 1; filter: brightness(1.3); }
  100% { transform: scale(1); opacity: 0.8; filter: brightness(1); }
}

/* 2. MOBILE OVERRIDES */
@media (max-width: 768px) {
  /* Prevent the big shadow boxes on mobile */
  .bg-cyan-500\\/20.blur-\\[120px\\] {
    display: none !important;
  }

  /* Simplify the VS for mobile performance but KEEP the pulse */
  .animate-\\[vs-pulse_1s_ease-in-out_infinite\\] {
    filter: none !important;
    text-shadow: 2px 2px 10px rgba(0,0,0,0.8) !important;
  }
}
`}} />
    {sortedFights.map((fight, idx) => {
  const isMain = idx === 0;
  const boutNumber = sortedFights.length - idx;
  const rId = String(fight.red_fighter_id);
const bId = String(fight.blue_fighter_id);

// Map the "Power Query" fields to the objects the UI expects
const red = {
  fighter_id: fight.red_fighter_id,
  name: fight.red_name,
  surname: fight.red_surname,
  image_url: fight.red_image,
  age: fight.red_age, // ADD THIS
  club_data: { logo_url: fight.red_club_logo }
};

const blue = {
  fighter_id: fight.blue_fighter_id,
  name: fight.blue_name,
  surname: fight.blue_surname,
  image_url: fight.blue_image,
  age: fight.blue_age, // ADD THIS
  club_data: { logo_url: fight.blue_club_logo }
};

  const isLive = fight.status === 'live';
  const isFinished = fight.status === 'finished';
  const winnerId = String(fight.winner || "");

  return (
    <Draggable key={String(fight.fight_id)} draggableId={String(fight.fight_id)} index={idx} isDragDisabled={!isAdmin || isLive}>
      {(provided, snapshot) => (
        <div 
          ref={provided.innerRef} 
          {...provided.draggableProps} 
          className={`relative mb-24 transition-all duration-500 ${snapshot.isDragging ? "z-50 scale-105" : ""}`}
        >
          {/* 1. CINEMATIC BACKGROUND HEADER (FIGHT + CLASS) */}
          <div className="absolute -top-16 md:-top-24 left-4 z-0 pointer-events-none select-none">
            <div className="flex items-baseline gap-2 md:gap-4">
              <span className="text-5xl md:text-[10rem] font-[1000] italic leading-none text-white/5 uppercase tracking-tighter">
                FIGHT{boutNumber}
              </span>
              </div>
          </div>

          {/* 2. THE ARMORED BADGE ASSEMBLY - WIDE TOP / NARROW BOTTOM */}
<div {...provided.dragHandleProps} className="flex justify-center mb-[-24px] relative z-30 px-2">
  <div 
    className="flex items-stretch shadow-[0_25px_50px_-12px_rgba(0,0,0,0.9)]"
    style={{ 
      /* Wide top (0% & 100%), narrow bottom (10% & 90%) */
      clipPath: 'polygon(0% 0%, 100% 0%, 92% 100%, 8% 100%)' 
    }}
  >
    
    {/* CLASS BLOCK - GOLD TIER */}
<div 
  className="bg-gradient-to-b from-[#eab308] via-[#fbbf24] to-[#a16207] flex flex-col items-center justify-center pl-10 pr-6 md:pl-14 md:pr-10 py-2 md:py-4 border-r-4 border-black/20"
>
  <span className="text-2xl md:text-4xl font-[1000] italic text-black leading-none">
    {fight.division || "A"}
  </span>
  <span className="text-[8px] md:text-[10px] font-black uppercase text-black/60 leading-none tracking-widest mt-0.5">CLASS</span>
</div>

    {/* STATUS BLOCK */}
    <div 
      className={`flex flex-col justify-center pl-6 pr-10 md:pl-12 md:pr-20 py-2 md:py-4 backdrop-blur-3xl border-y border-r border-white/10
        ${isLive ? 'bg-red-600/90 text-white' : 
          isMain ? 'bg-zinc-900 text-yellow-500 border-yellow-500/50' : 
          'bg-black/80 text-white'}`}
    >
      <div className="flex items-center gap-2 md:gap-4">
        {isLive && <span className="w-2 h-2 rounded-full bg-white animate-ping" />}
        <span className="text-xs md:text-xl font-[1000] italic uppercase tracking-wider">
          {isLive ? "LIVE NOW" : isMain ? "MAIN EVENT" : `FIGHT ${boutNumber}`}
        </span>
      </div>
      
      <div className="flex items-center gap-2 mt-0.5 opacity-50">
        <span className="text-[7px] md:text-[9px] font-black uppercase tracking-[0.3em]">
          {fight.division ? `${fight.division} CLASS` : "PRO DIVISION"}
        </span>
        <div className="h-[1px] w-4 bg-current" />
        <span className="text-[7px] md:text-[9px] font-black uppercase tracking-[0.3em]">
          {fight.weightClass || "OPEN"} KG
        </span>
      </div>
    </div>
  </div> 

</div>

          {/* 3. THE FIGHT POSTER CARD */}
          <div className={`relative overflow-hidden rounded-[3rem] md:rounded-[4rem] bg-[#050505] border-2 transition-all duration-700 
            ${isLive ? 'border-red-600 shadow-[0_0_100px_rgba(220,38,38,0.3)]' : 'border-white/5 shadow-2xl'}`}>
            
          {/* MINIMALIST ATMOSPHERE */}
<div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
  {/* Just the subtle grit texture for depth */}
  <div className="absolute inset-0 opacity-[0.05] bg-[url('https://www.transparenttextures.com/patterns/asfalt-dark.png')]" />
</div>
            <div className="relative flex flex-row items-stretch justify-between min-h-[340px] md:min-h-[560px]">
              
        {/* --- RED CORNER --- */}
<div
  className={`relative flex-1 flex flex-col items-center transition-all duration-1000 group h-fit pt-10
  ${isFinished && winnerId !== rId && winnerId !== "draw" && winnerId !== "" ? 'grayscale contrast-75 opacity-30 scale-95' : 'scale-100'}`}
>
  <Link to={`/fighters/${rId}`} className="relative block w-full max-w-[340px]">
    <div className="relative w-full aspect-[2/3] overflow-hidden rounded-none shadow-2xl bg-zinc-950/40">
      
      {/* RED WINNER & METHOD STAMP - COMPACT & TIGHT */}
{isFinished && winnerId === rId && (
  <div className="absolute inset-0 z-50 flex items-center justify-center pt-28 md:pt-36 pointer-events-none">
    <div className="flex flex-col items-center animate-in zoom-in duration-300">
      
      {/* MAIN WINNER STAMP (Tighter Padding) */}
      <div className="bg-yellow-500 text-black font-[1000] italic text-xl md:text-3xl px-4 py-1 uppercase tracking-tighter shadow-[0_12px_30px_rgba(0,0,0,0.6)] -rotate-12 border-[2.5px] border-black">
        WINNER
      </div>

      {/* METHOD OVERLAP (Tightened & Tucked Under) */}
      {fight.method && (
        <div className="bg-black text-white px-2 py-0.5 mt-[-6px] -rotate-12 border border-white/20 shadow-lg relative z-10">
          <span className="text-[8px] md:text-[10px] font-[1000] italic uppercase tracking-[0.1em] block">
            {fight.method}
          </span>
        </div>
      )}
    </div>
  </div>
)}

      {/* LOGO & IMAGE (Existing Logic) */}
      {red?.club_data?.logo_url && (
        <div className="absolute top-1.5 left-1.5 z-40 w-10 h-10 md:w-16 md:h-16 drop-shadow-xl overflow-hidden rounded-full">
          <img src={`${API_BASE_URL}/uploads/clubs/${red.club_data.logo_url}`} className="w-full h-full object-contain" alt="" />
        </div>
      )}
      <img
        src={getFighterImage(red)}
        className="w-full h-full object-cover object-[50%_15%] filter contrast-110 brightness-95 relative z-10"
        alt=""
      />
      {/* RED NAME BOX - MATCHED TO BLUE SIDE */}
<div className="absolute bottom-0 left-0 right-0 z-30 px-3 pb-4 pt-16 bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none">
  <div className="flex flex-col items-start w-full text-left">
    <h3 className="font-[1000] italic uppercase leading-[0.8] tracking-tighter bg-gradient-to-b from-white via-gray-200 to-gray-400 bg-clip-text text-transparent 
  /* Mobile: 10% reduction (1.1rem -> 1rem) | Desktop: Kept at 3xl / 2.25rem */
  text-[clamp(1rem,3.2vw,2.25rem)] md:text-3xl lg:text-[2.25rem]
  /* Symmetry with the blue side */
  whitespace-normal break-words max-w-[88%] text-left drop-shadow-2xl"
>
  {red?.name}
</h3>
    <span className="text-[10px] md:text-sm font-black italic text-white/40 uppercase tracking-[0.25em] mt-1 block truncate">
      {red?.surname}
    </span>
{/* AGE BADGE */}
    {red?.age && (
    <span className="text-[10px] md:text-sm font-bold text-gold-500/80 italic">
      • {red.age} YRS
    </span>
  )}
  </div>
</div>
    </div>
  </Link>

  {/* RED CORNER BOTTOM */}
  <div className="w-full max-w-[340px] relative z-40">
    <div className="w-full bg-red-600 h-[4px]"></div>
    <div className="flex items-center justify-between mt-1 gap-2">
      {getFightRank(rId, fight.division) && (
        <div className="bg-red-600 px-3 py-1 border-b border-white/20 min-w-[70px]" style={{ clipPath: "polygon(0 0, 100% 0, 85% 100%, 0 100%)" }}>
          <div className="flex items-baseline gap-1 text-white italic">
            <span className="text-[8px] font-black opacity-60">RANK</span>
            <span className="text-xl md:text-2xl font-[1000]">{getFightRank(rId, fight.division)}</span>
          </div>
        </div>
      )}
      <div className="w-full md:w-auto flex flex-col items-start md:ml-4 gap-2">
        <EventVote side="red" fightId={fight.fight_id} fighterRedId={rId} fighterBlueId={bId} userVote={liveVotes[String(fight.fight_id)]?.user_vote} onVotesLoaded={(data) => handleVotesUpdate(fight.fight_id, data)} onRefreshVotes={fetchFights} />
      </div>
    </div>
  </div>
</div>
              {/* THE AGGRESSIVE VS DIVIDER */}
<div className="absolute inset-0 pointer-events-none flex items-center justify-center z-30">
  <div className="relative flex items-center justify-center scale-110">
    
    {/* OUTER GLOW (PULSING) */}
    <span className="absolute blur-2xl text-cyan-500/30 text-6xl md:text-[10rem] font-[1000] italic uppercase animate-[vs-pulse_1s_ease-in-out_infinite]">
      VS
    </span>

    {/* THE MAIN TEXT - BRUSHED METAL LOOK */}
    <span className="relative z-10 text-6xl md:text-[10rem] font-[1000] italic tracking-tighter uppercase 
      bg-gradient-to-b from-white to-gray-600 bg-clip-text text-transparent
      drop-shadow-[0_10px_10px_rgba(0,0,0,1)]
      animate-[vs-pulse_1s_ease-in-out_infinite]">
      VS
    </span>
  </div>
</div>
     {/* --- BLUE CORNER --- */}
<div
  className={`relative flex-1 flex flex-col items-center transition-all duration-1000 group h-fit pt-10
  ${isFinished && winnerId !== bId && winnerId !== "draw" && winnerId !== "" ? 'grayscale contrast-75 opacity-30 scale-95' : 'scale-100'}`}
>
  <Link to={`/fighters/${bId}`} className="relative block w-full max-w-[340px]">
    <div className="relative w-full aspect-[2/3] overflow-hidden rounded-none shadow-2xl bg-zinc-950/40">
      
      {/* BLUE WINNER & METHOD STAMP - COMPACT & TIGHT */}
{isFinished && winnerId === bId && (
  <div className="absolute inset-0 z-50 flex items-center justify-center pt-28 md:pt-36 pointer-events-none">
    <div className="flex flex-col items-center animate-in zoom-in duration-300">
      
      {/* MAIN WINNER STAMP (Tighter Padding) */}
      <div className="bg-yellow-500 text-black font-[1000] italic text-xl md:text-3xl px-4 py-1 uppercase tracking-tighter shadow-[0_12px_30px_rgba(0,0,0,0.6)] rotate-12 border-[2.5px] border-black">
        WINNER
      </div>

      {/* METHOD OVERLAP (Tightened & Tucked Under) */}
      {fight.method && (
        <div className="bg-black text-white px-2 py-0.5 mt-[-6px] rotate-12 border border-white/20 shadow-lg relative z-10">
          <span className="text-[8px] md:text-[10px] font-[1000] italic uppercase tracking-[0.1em] block">
            {fight.method}
          </span>
        </div>
      )}
    </div>
  </div>
)}
      {/* LOGO & IMAGE (Existing Logic) */}
      {blue?.club_data?.logo_url && (
        <div className="absolute top-1.5 right-1.5 z-40 w-10 h-10 md:w-16 md:h-16 drop-shadow-xl overflow-hidden rounded-full">
          <img src={`${API_BASE_URL}/uploads/clubs/${blue.club_data.logo_url}`} className="w-full h-full object-contain" alt="" />
        </div>
      )}
      <img
        src={getFighterImage(blue)}
        className="w-full h-full object-cover object-[50%_15%] filter contrast-110 brightness-95 relative z-10"
        alt=""
      />
      {/* BLUE NAME BOX - FINAL FIX */}
<div className="absolute bottom-0 left-0 right-0 z-30 px-3 pb-4 pt-16 text-right bg-gradient-to-t from-black via-black/80 to-transparent pointer-events-none">
  <div className="flex flex-col items-end w-full text-right">
    <h3 className="font-[1000] italic uppercase leading-[0.8] tracking-tighter bg-gradient-to-b from-white via-gray-200 to-gray-400 bg-clip-text text-transparent 
  /* Mobile: 10% reduction (1.1rem -> 1rem) | Desktop: Kept at 3xl / 2.25rem */
  text-[clamp(1rem,3.2vw,2.25rem)] md:text-3xl lg:text-[2.25rem]
  /* Exact match to Red side */
  whitespace-normal break-words max-w-[88%] ml-auto text-right drop-shadow-2xl"
>
  {blue?.name}
</h3>
    <span className="text-[10px] md:text-sm font-black italic text-white/40 uppercase tracking-[0.25em] mt-1 block truncate">
  {blue?.surname}
</span>
{blue?.age && (
    <span className="text-[10px] md:text-sm font-bold text-gold-500/80 italic">
     {blue.age} YRS • 
    </span>
  )}
  </div>
</div>
    </div>
  </Link>

  {/* BLUE CORNER BOTTOM */}
  <div className="w-full max-w-[340px] relative z-40">
    <div className="w-full bg-blue-600 h-[4px]"></div>
    <div className="flex items-center flex-row-reverse justify-between mt-1 gap-2">
      {getFightRank(bId, fight.division) && (
        <div className="bg-blue-600 px-3 py-1 border-b border-white/20 text-right min-w-[70px]" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 15% 100%)" }}>
          <div className="flex items-baseline gap-1 text-white italic">
            <span className="text-[8px] font-black opacity-60">RANK</span>
            <span className="text-xl md:text-2xl font-[1000]">{getFightRank(bId, fight.division)}</span>
          </div>
        </div>
      )}
      <div className="w-full md:w-auto flex flex-col items-end md:mr-4 gap-2 text-right">
        <EventVote side="blue" fightId={fight.fight_id} fighterRedId={rId} fighterBlueId={bId} userVote={liveVotes[String(fight.fight_id)]?.user_vote} onVotesLoaded={(data) => handleVotesUpdate(fight.fight_id, data)} onRefreshVotes={fetchFights} />
      </div>
    </div>
  </div>
</div>
</div> {/* END OF FIGHTERS ROW (RED + VS + BLUE) */}

        {/* 4. THE INDUSTRIAL INFO BAR (PROPERLY WRAPPING THE FULL WIDTH) */}
        <div className="w-full bg-white/[0.04] flex flex-col border-t border-white/10 backdrop-blur-md relative z-50">
          
         {/* THE TOTAL PROGRESS BAR - UNIFIED DATA STRIP */}
<div className="w-full bg-black/40 relative pb-10 pt-6 px-4 border-b border-white/5">
  {(() => {
    const fid = String(fight.fight_id);
    const data = liveVotes[fid] || fight || {};
    
    const redV = Number(data.red_votes || 0);
    const blueV = Number(data.blue_votes || 0);
    const total = redV + blueV;

    const redP = total > 0 ? Math.round((redV / total) * 100) : 50;
    const blueP = total > 0 ? 100 - redP : 50;

    return (
      <div className="max-w-4xl mx-auto relative">
        {/* THE MAIN PROGRESS CONTAINER */}
        <div className="h-8 md:h-10 w-full bg-zinc-950 rounded-full overflow-hidden flex border border-white/10 p-[2px] shadow-[0_0_30px_rgba(0,0,0,0.5)] relative">
          
          {/* RED SIDE */}
          <div 
            className="h-full bg-gradient-to-r from-red-900 via-red-600 to-red-500 transition-all duration-1000 ease-out flex items-center px-4" 
            style={{ width: `${redP}%` }}
          >
            <div className="flex flex-col items-start drop-shadow-md">
              <span className="text-[11px] md:text-sm font-[1000] text-white italic leading-none">
                {redP}%
              </span>
              <span className="text-[8px] font-black text-white/40 uppercase tracking-tighter mt-0.5">
                {redV} VOTES
              </span>
            </div>
          </div>

          {/* BLUE SIDE */}
          <div 
            className="h-full bg-gradient-to-l from-blue-900 via-blue-600 to-blue-500 transition-all duration-1000 ease-out flex items-center justify-end px-4" 
            style={{ width: `${blueP}%` }}
          >
            <div className="flex flex-col items-end drop-shadow-md">
              <span className="text-[11px] md:text-sm font-[1000] text-white italic leading-none">
                {blueP}%
              </span>
              <span className="text-[8px] font-black text-white/40 uppercase tracking-tighter mt-0.5">
                {blueV} VOTES
              </span>
            </div>
          </div>

          {/* --- THE CENTRAL TOTAL BADGE (THE DIVIDER) --- */}
          <div 
            className="absolute top-0 bottom-0 w-12 md:w-16 z-40 bg-zinc-900 border-x-2 border-white/20 shadow-[0_0_20px_rgba(0,0,0,1)] flex flex-col items-center justify-center transition-all duration-1000"
            style={{ 
              left: `${redP}%`, 
              transform: 'translateX(-50%)' 
            }}
          >
            {/* Glossy overlay for the center piece */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
            
            <span className="text-[7px] font-black text-white/30 uppercase tracking-[0.1em] leading-none">TOTAL</span>
            <span className="text-[11px] md:text-sm font-[1000] text-cyan-500 italic leading-none mt-0.5">
              {total}
            </span>
          </div>
        </div>

        {/* Subtle Bottom Glow to ground the element */}
        <div 
          className="absolute -bottom-2 h-[1px] bg-cyan-500/20 blur-md transition-all duration-1000"
          style={{ 
            left: '10%', 
            right: '10%',
            opacity: total > 0 ? 1 : 0 
          }} 
        />
      </div>
    );
  })()}
</div>
          {/* METADATA & ADMIN CONTROLS AREA */}
          <div className="px-6 md:px-14 py-4 md:py-8 flex flex-wrap justify-between items-center gap-4">
            <div className="flex flex-wrap items-center gap-6 md:gap-12">
              <div className="flex items-center gap-2 text-[10px] md:text-xs font-black uppercase text-gray-400 tracking-[0.2em] border-l border-white/10 pl-6 md:pl-12">
                <span>{fight.rounds || "3x2"} MIN</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] md:text-xs font-black uppercase text-white tracking-[0.2em] border-l border-white/10 pl-6 md:pl-12">
                <span>{fight.weightClass || "CATCHWEIGHT"} KG</span>
              </div>
              
              
            </div>

            {isAdmin && (
  <div className="flex flex-col md:flex-row items-center gap-3 bg-black/40 p-2 rounded-2xl border border-white/5">
    {/* Status Selector */}
    <select 
      value={fight.status || "scheduled"} 
      onChange={(e) => handleUpdateStatus(fight.fight_id, { status: e.target.value })} 
      className="bg-zinc-900 border border-white/10 text-[9px] font-black uppercase px-3 py-2 rounded-xl text-white"
    >
      <option value="scheduled">Scheduled</option>
      <option value="live">Live</option>
      <option value="finished">Finished</option>
    </select>

    {/* Winner & Method - Only show if Finished */}
{fight.status === 'finished' && (
  <>
    <select 
  value={fight.winner || ""} 
  onChange={(e) => handleUpdateStatus(fight.fight_id, { winner: e.target.value })} 
  className="..."
>
  <option value="">Select Winner</option>
  <option value={rId}>{red?.name}</option>
  <option value={bId}>{blue?.name}</option>
  <option value="draw">Draw</option>
</select>

    <select 
  value={fight.method || ""} 
  onChange={(e) => handleUpdateStatus(fight.fight_id, { method: e.target.value })} 
  className="..."
>
  <option value="">Method</option>
  <option value="U-DECISION">U-Decision</option>
  <option value="S-DECISION">S-Decision</option>
  <option value="KO">KO</option>
  <option value="TKO">TKO</option>
  <option value="STOPPAGE">Stoppage</option>
</select>
  </>
)}

    {/* Delete Button */}
    <button 
      onClick={async () => {
        if (!window.confirm("Delete?")) return;
        try {
          await axios.delete(`${API_BASE_URL}/events/fights/${fight.fight_id}`, { headers: { Authorization: `Bearer ${token}` } });
          fetchFights();
        } catch (err) { alert("Error"); }
      }}
      className="p-2 rounded-xl bg-red-600/10 text-red-500"
    >
      <Trash size={16} />
    </button>
  </div>
)}
        </div>
      </div>
    </div>    
</div>
  )}
</Draggable>
);
  })}
  {provided.placeholder}
</div>
                )}
              </StrictModeDroppable>
            </DragDropContext>
          ) : (
            <div className="text-center py-40 border-2 border-dashed border-white/5 rounded-[4rem]">
              <Fire size={48} className="text-neutral-800 mx-auto mb-6" />
              <p className="font-black uppercase tracking-[0.4em] text-neutral-600">No Bouts Scheduled</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
